import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { getStrokeString } from '../Utils'
import { EMPTY, formatDuration, formatNumber } from '../Shared/detailFormat'

// lapDistanceUnit: 0 = metric, 1 = imperial.
const lapUnit = (lap) => (lap.lapDistanceUnit === 1 ? 'yd' : 'm')

// Laps by stroke, so a mis-detected stroke stands out before scrolling the table.
export const lapStrokeCounts = (laps) => {
  const counts = {}
  ;(laps || []).forEach((lap) => {
    const name = getStrokeString(lap?.strokeType)
    counts[name] = (counts[name] || 0) + 1
  })
  return Object.entries(counts).sort((a, b) => b[1] - a[1])
}

// Keeps each lap's position in lapSamplesData, which is what "#" shows and
// what Edit writes back to, however the list is filtered.
export const filterLapsByStroke = (laps, stroke) =>
  (laps || [])
    .map((lap, index) => ({ lap, index }))
    .filter(({ lap }) => lap && (stroke === null || getStrokeString(lap.strokeType) === stroke))

// A long swim can have hundreds of laps: the table scrolls inside the section
// with its header pinned, and the stroke badges filter it.
const LapTableComponent = ({ laps, onEdit }) => {
  const [stroke, setStroke] = useState(null)

  if (!laps || laps.length === 0) {
    return <p className="text-muted mb-0">No lap samples</p>
  }
  const firstStart = laps.find((lap) => typeof lap?.startDate === 'number')?.startDate
  const rows = filterLapsByStroke(laps, stroke)

  return (
    <div>
      <p className="mb-2">
        {lapStrokeCounts(laps).map(([name, count]) => (
          <button
            key={name}
            type="button"
            className={'badge border me-1 detail-badge-toggle ' +
              (stroke === name ? 'text-bg-primary' : 'text-bg-light')}
            title={stroke === name ? 'Show all laps' : `Show only ${name} laps`}
            onClick={() => setStroke(stroke === name ? null : name)}
          >
            {name}: {count}
          </button>
        ))}
        {stroke !== null && (
          <small className="text-muted ms-1">
            {rows.length} of {laps.length} laps ·{' '}
            <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={() => setStroke(null)}>
              show all
            </button>
          </small>
        )}
      </p>
      <div className="detail-long-list">
        <table className="table table-sm table-striped mb-0">
          <thead>
            <tr>
              <th>#</th>
              <th>Start</th>
              <th>Stroke</th>
              <th>Length</th>
              <th title="totalSwimTime">Swim time</th>
              <th title="elapsedTime (Garmin)">Elapsed</th>
              <th title="strokeCount: per length, single-arm cycles for free and back. (strokeCountRaw): Garmin's raw both-arm count">Strokes</th>
              <th title="distance: cumulative so far (Garmin only)">Total dist.</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ lap, index }) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>
                  {typeof lap.startDate === 'number' && firstStart !== undefined
                    ? '+' + formatDuration(lap.startDate - firstStart)
                    : EMPTY}
                </td>
                <td>{getStrokeString(lap.strokeType)}</td>
                <td>{lap.lapLength}{lapUnit(lap)}</td>
                <td>{formatNumber(lap.totalSwimTime, 2)}s</td>
                <td>
                  {lap.elapsedTime !== undefined
                    ? formatNumber(lap.elapsedTime, 2) + 's'
                    : EMPTY}
                </td>
                <td>
                  {lap.strokeCount ?? EMPTY}
                  {lap.strokeCountRaw !== undefined && (
                    <span className="text-muted"> ({lap.strokeCountRaw})</span>
                  )}
                </td>
                <td>
                  {lap.distance !== undefined
                    ? formatNumber(lap.distance, 1) + lapUnit(lap)
                    : EMPTY}
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0"
                    onClick={() => onEdit(lap, index)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

LapTableComponent.propTypes = {
  laps: PropTypes.array,
  onEdit: PropTypes.func.isRequired
}

export default LapTableComponent
